import { useSelector } from "react-redux";
import { ObjectTarget, } from "../../../app/permissions";
import { assertThumbnailUrl, getBackendUrl, LoadState } from "../../../redux/common";
import { Avatar, Box, Stack, Typography } from "@mui/material";
import { stringAvatar, stringToColor } from "../../user/avatarUtils";
import { ConversationInfo, ConversationMessage, timeSimplifier } from "../../../redux/conversation/conversationSlice";
import { ObjectRetrieveRequest, retrieveObject, selectObjectCache } from "../../../redux/object/objectCacheSlice";
import { useEffect, useMemo, useState } from "react";
import { useAppDispatch } from "../../../app/hooks";
import { NamedObject } from "../../../redux/object/namedObject";
import { getMessagePriorityColor, getMessagePriorityName, MessagePriority } from "../../../redux/conversation/messagePriority";
import { MessageContent } from "./MessageContent";

interface MessageTileOtherProps {
    conversation: ConversationInfo;
    message: ConversationMessage;
    content?: React.ReactNode;
}

export const MessageTileOther = (props: MessageTileOtherProps) => {
    const msg = props.message;
    const dt = timeSimplifier( msg.processed );
    const dispatch = useAppDispatch();

    const message = <MessageContent message={msg} content={props.content} align="left"/>;

    const [object, setObject] = useState(undefined as undefined | NamedObject);
    const objectCache = useSelector(selectObjectCache);

    const request = useMemo((): ObjectRetrieveRequest => {
        return {
            objectTarget: ObjectTarget.USER,
            targetId: `${msg.userId}`,
        };
    }, [msg.userId]);

    useEffect(() => {
        if(!msg.userId) {
            return ;
        }
        const foundObject = (objectCache.cache.users[request.targetId]);
        if(foundObject) {
            setObject( NamedObject.user(foundObject));
        }
        if (!foundObject) {
            let state = LoadState.NotStarted;
            if ( objectCache.requests[request.objectTarget + ":" + request.targetId]
                    && objectCache.requests[request.objectTarget + ":" + request.targetId].state ) {
                state = objectCache.requests[request.objectTarget + ":" + request.targetId].state;
            }
            if (state == LoadState.NotStarted) {
                dispatch(retrieveObject(request));
            }
        }
    }, [request, objectCache]);

    let image = undefined;
    let name = msg.partyId || "Unknown User";
    let titleColor = stringToColor(name);
    if (object) {
        image = assertThumbnailUrl(object.url);
        name = object.display;
        titleColor = stringToColor(name);
    } else {
        name = "Sistema";
        image = getBackendUrl() + "/api/image/priza.png";
        titleColor = "purple";
    }

    return <Box sx={{ marginRight: 16, marginY: 1 }}>
        <Stack direction="row" spacing={1}>
            <Box sx={{ width: 32, height: 32, alignContent: "center", justifyContent: "center", justifyItems: "center" }}>
                <Avatar sx={{ width: 32, height: 32, m: 0, border: "1px solid gray", backgroundColor: stringToColor(name) }}
                    variant='circular' alt={`${name}`} src={`${image}`}>
                    <Typography variant="h6" component="span" fontSize={13}>
                        {stringAvatar(name)}
                    </Typography>
                </Avatar>
            </Box>
            <Stack direction="column" spacing={0}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="subtitle2" component="span" fontSize={11} color={titleColor} sx={{ marginY: "-2px" }}>
                        {name}
                    </Typography>
                    <Box sx={{ flexGrow: 1 }} />
                    <Typography variant="caption" component="span" fontSize={9} sx={{ textAlign: "right", color: "gray", marginY: "-2px" }}>
                        {dt}
                    </Typography>
                </Stack>
                <Box key={dt}
                    sx={{ padding: 1, 
                        borderRadius: 2, 
                        border: "1px solid", 
                        borderColor: (theme) => 
                            msg.messagePriority == MessagePriority.Regular ? "black" :
                            getMessagePriorityColor(theme, msg.messagePriority).contrastText, 
                        backgroundColor: (theme) => 
                            msg.messagePriority == MessagePriority.Regular ? "lightgray" :
                            getMessagePriorityColor(theme, msg.messagePriority).main,
                        minWidth: 300 
                    }}>
                        {
                            msg.messagePriority != MessagePriority.Regular &&
                            <Stack direction="row" alignItems="center">
                                <Box sx={{ flexGrow: 1 }} />
                                <Typography variant="caption" component="div" sx={{ 
                                    color: (theme) => getMessagePriorityColor(theme, msg.messagePriority).contrastText,
                                    fontWeight: "bold",
                                }}>
                                    {`[${getMessagePriorityName(msg.messagePriority)}]`}
                                </Typography>
                            </Stack>
                        }
                        <Typography variant="body2" component="span" sx={{
                            color: (theme) =>
                                msg.messagePriority == MessagePriority.Regular ? "black" :
                                getMessagePriorityColor(theme, msg.messagePriority).contrastText,
                            overflowWrap: "anywhere"
                        }}>
                            {message}
                        </Typography>
                </Box>
            </Stack>
        </Stack>
    </Box>;
};